// src/components/core/entity/EntityChannelsCard/ContactPersonModal/EditModal.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useMemo, useState } from 'react';
import { Form } from 'antd';
import { UserOutlined } from '@ant-design/icons';
import GenericModal from 'components/core/GenericModal';
import appService from 'services/appService';
import entitiesService from 'services/entitiesService';
import axiosService from 'services/axiosService';
import { API } from 'constants/Api';
import { returnOnlyNumbers } from 'utils/helpers';
import { getPhoneMeta } from 'utils/phone';
import { useSimpleFormContext } from 'components/SimpleComponents/SimpleFormProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleEmail from 'components/SimpleComponents/SimpleEmail';
import SimplePhone from 'components/SimpleComponents/SimplePhone';
import SimpleTextArea from 'components/SimpleComponents/SimpleTextArea';
import SimpleSwitch from 'components/SimpleComponents/SimpleSwitch';

// *******************************************************************************
// STEP CONTENT COMPONENT
// *******************************************************************************
const EditPersonContent = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const { form, goFocus } = useSimpleFormContext();

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL VARIABLES I
  // ─────────────────────────────────────────────────────────────────────────────
  // Watch both fields for dynamic required logic (at least one must be kept)
  const watchPhone = Form.useWatch('personPhone', form);
  const watchEmail = Form.useWatch('personEmail', form);

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [phoneEmailState, setPhoneEmailState] = useState({
    hasPhone: false,
    hasEmail: false,
  });

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const timer = setTimeout(() => {
      goFocus?.('personName', true);
    }, 150);
    return () => clearTimeout(timer);
  }, [goFocus]);

  // Track phone/email values for dynamic required (at least one must be kept)
  useEffect(() => {
    const phoneVal = typeof watchPhone === 'object' ? watchPhone?.value : watchPhone;
    const emailVal = typeof watchEmail === 'object' ? watchEmail?.value : watchEmail;
    setPhoneEmailState({
      hasPhone: !!phoneVal && phoneVal.trim() !== '',
      hasEmail: !!emailVal && emailVal.trim() !== '',
    });
  }, [watchPhone, watchEmail]);

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────

  // ─────────────────────────────────────────────────────────────────────────────
  // 7. LOCAL VARIABLES II
  // ─────────────────────────────────────────────────────────────────────────────
  // Dynamic required: both required when neither has value, optional when the other has value
  const phoneRequired = !phoneEmailState.hasEmail;
  const emailRequired = !phoneEmailState.hasPhone;

  // ─────────────────────────────────────────────────────────────────────────────
  // 8. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <SimpleInput name="personName" label="name" required maxLength={100} />

      <SimplePhone
        name={['personPhone', 'value']}
        label="phone"
        international
        required={phoneRequired}
      />

      <SimpleEmail
        name={['personEmail', 'value']}
        label="email"
        placeholder="contact@example.com"
        required={emailRequired}
      />

      <SimpleTextArea
        name="note"
        label="note"
        rows={2}
        maxLength={200}
        placeholder="note_placeholder"
      />

      {/*<SimpleSwitch name="isPrimary" label="favorite" showLabel />*/}
    </>
  );
};

// *******************************************************************************
// STEP CONFIGURATION
// *******************************************************************************
function editPersonStep() {
  return {
    stepTitle: 'edit_person_data',
    content: () => <EditPersonContent />,
  };
}

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
const EditPersonModal = ({
  show,
  setShow,
  afterClose,
  parentEntityId,
  contact,
  linkedEntity,
  onSuccess,
}) => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. LOCAL VARIABLES
  // ─────────────────────────────────────────────────────────────────────────────
  const steps = useMemo(() => [editPersonStep()], []);

  // Extract existing phone/email from linked entity
  const existingPhone = linkedEntity?.contacts?.find(c => c.contactType === 'phone');
  const existingEmail = linkedEntity?.contacts?.find(c => c.contactType === 'email');

  const initialValues = useMemo(
    () => ({
      personName: linkedEntity?.name || contact?.value || '',
      personPhone: existingPhone
        ? { value: existingPhone.value || '', countryCode: existingPhone.countryCode || 'BR' }
        : { value: '', countryCode: 'BR' },
      personEmail: existingEmail ? { value: existingEmail.value || '' } : { value: '' },
      note: contact?.note || '',
      isPrimary: contact?.isPrimary || false,
      // Hidden references for handleFinish
      _existingPhoneId: existingPhone?._id || null,
      _existingEmailId: existingEmail?._id || null,
    }),
    [linkedEntity, contact, existingPhone, existingEmail]
  );

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleFinish = async ({ form }) => {
    try {
      const values = form.getFieldsValue(true);
      const linkedId = contact.linkedEntityId;

      // Validate name
      if (!values.personName?.trim()) {
        appService.message({ flag: 'w', text: 'field_required' });
        return false;
      }

      // ── Extract current form values ──────────────────────────────────────
      const newName = values.personName.trim();
      const newNote = (values.note || '').trim();
      const newIsPrimary = !!values.isPrimary;

      const rawPhone = values.personPhone;
      const phoneValue = typeof rawPhone === 'object' ? rawPhone?.value : rawPhone;
      const phoneCC = (typeof rawPhone === 'object' ? rawPhone?.countryCode : null) || 'BR';
      const cleanPhone = phoneValue ? returnOnlyNumbers(phoneValue) : '';

      const rawEmail = values.personEmail;
      const emailValue = typeof rawEmail === 'object' ? rawEmail?.value : rawEmail;
      const cleanEmail = emailValue?.trim() || '';

      // At least one contact (phone or email) must be kept
      if (!cleanPhone && !cleanEmail) {
        appService.message({ flag: 'w', text: 'at_least_one_required' });
        return false;
      }

      // ── Detect what actually changed ─────────────────────────────────────
      const origName = linkedEntity?.name || contact?.value || '';
      const origNote = contact?.note || '';
      const origPhone = existingPhone?.value || '';
      const origPhoneCC = existingPhone?.countryCode || 'BR';
      const origEmail = existingEmail?.value || '';
      const origIsPrimary = contact?.isPrimary || false;

      const nameChanged = newName !== origName;
      const noteChanged = newNote !== origNote;
      const phoneChanged = cleanPhone !== origPhone || (cleanPhone && phoneCC !== origPhoneCC);
      const emailChanged = cleanEmail !== origEmail;
      const isPrimaryChanged = newIsPrimary !== origIsPrimary;

      // Nothing changed → just close
      if (!nameChanged && !noteChanged && !phoneChanged && !emailChanged && !isPrimaryChanged) {
        return true;
      }

      await appService.message({ flag: 'i', text: 'saving_data', key: 'editPerson' });

      const url = API.ENTITIES.URL.replace(':_id?', linkedId);
      const existPhoneId = values._existingPhoneId;
      const existEmailId = values._existingEmailId;

      // ── 1. Update entity name if changed ─────────────────────────────────
      if (nameChanged) {
        await axiosService.put({ url, data: { name: newName, fullName: newName } });
      }

      // ── 2. Handle phone contact on linked entity (only if changed) ───────
      if (phoneChanged) {
        const phoneMeta = cleanPhone ? getPhoneMeta(cleanPhone, phoneCC) : null;
        const phoneType = phoneMeta?.isMobile
          ? 'mobile'
          : phoneMeta?.isLandline
            ? 'landline'
            : 'mobile';

        if (existPhoneId && cleanPhone) {
          // Update existing phone in-place (backend detects _id and updates)
          await entitiesService.addToSet({
            _id: linkedId,
            operator: '$push',
            path: 'contacts',
            data: {
              _id: existPhoneId,
              contactType: 'phone',
              value: cleanPhone,
              countryCode: phoneCC,
              phoneType,
              isPrimary: true,
            },
          });
        } else if (!existPhoneId && cleanPhone) {
          // Add new phone
          await entitiesService.addToSet({
            _id: linkedId,
            operator: '$push',
            path: 'contacts',
            data: {
              contactType: 'phone',
              value: cleanPhone,
              countryCode: phoneCC,
              phoneType,
              isPrimary: true,
            },
          });
        } else if (existPhoneId && !cleanPhone) {
          // Remove phone
          await entitiesService.addToSet({
            _id: linkedId,
            operator: '$pull',
            path: 'contacts',
            data: { subdocumentId: existPhoneId },
          });
        }
      }

      // ── 3. Handle email contact on linked entity (only if changed) ───────
      if (emailChanged) {
        if (existEmailId && cleanEmail) {
          // Update existing email in-place (backend detects _id and updates)
          await entitiesService.addToSet({
            _id: linkedId,
            operator: '$push',
            path: 'contacts',
            data: {
              _id: existEmailId,
              contactType: 'email',
              value: cleanEmail,
              countryCode: 'BR',
              isPrimary: true,
            },
          });
        } else if (!existEmailId && cleanEmail) {
          // Add new email
          await entitiesService.addToSet({
            _id: linkedId,
            operator: '$push',
            path: 'contacts',
            data: {
              contactType: 'email',
              value: cleanEmail,
              countryCode: 'BR',
              isPrimary: true,
            },
          });
        } else if (existEmailId && !cleanEmail) {
          // Remove email
          await entitiesService.addToSet({
            _id: linkedId,
            operator: '$pull',
            path: 'contacts',
            data: { subdocumentId: existEmailId },
          });
        }
      }

      // ── 4. Update parent contact (if name, note, or isPrimary changed) ──
      if (nameChanged || noteChanged || isPrimaryChanged) {
        // Update in-place (backend detects _id and updates the subdocument)
        await entitiesService.addToSet({
          _id: parentEntityId,
          operator: '$push',
          path: 'contacts',
          data: {
            _id: contact._id,
            contactType: 'person',
            value: newName,
            linkedEntityId: contact.linkedEntityId,
            note: newNote,
            countryCode: contact.countryCode || 'BR',
            isPrimary: newIsPrimary,
          },
        });
      }

      // ── 5. Build result for parent component (no extra GET needed) ──────
      const updatedContact = {
        ...contact,
        value: newName,
        note: newNote,
        isPrimary: newIsPrimary,
      };

      // Build fresh _linkedEntity locally from original + applied changes
      const freshEntity = buildFreshLinkedEntity(
        linkedEntity,
        { nameChanged, phoneChanged, emailChanged },
        { newName, cleanPhone, phoneCC, cleanEmail },
        { existPhoneId, existEmailId }
      );

      await appService.message({ flag: 's', text: 'saved_successfully', key: 'editPerson' });
      onSuccess?.(freshEntity, updatedContact);
      return true;
    } catch (error) {
      await appService.message({ flag: 'e', text: 'error_saving', key: 'editPerson' });
      return false;
    }
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. UI
  // ─────────────────────────────────────────────────────────────────────────────
  return (
    <GenericModal
      show={show}
      setShow={setShow}
      afterClose={afterClose}
      icon={<UserOutlined />}
      title="edit_person_data"
      steps={steps}
      initialValues={initialValues}
      onFinish={handleFinish}
      width={500}
      maskClosable={false}
    />
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default EditPersonModal;

// *******************************************************************************
// HELPERS
// *******************************************************************************

/**
 * Build a fresh _linkedEntity object locally from the original + applied changes.
 * Avoids an extra GET request to the backend.
 */
function buildFreshLinkedEntity(originalEntity, changes, values, ids) {
  if (!originalEntity) return null;

  const { nameChanged, phoneChanged, emailChanged } = changes;
  const { newName, cleanPhone, phoneCC, cleanEmail } = values;
  const { existPhoneId, existEmailId } = ids;

  // Start from original entity
  const fresh = { ...originalEntity };

  // Update name fields
  if (nameChanged) {
    fresh.name = newName;
    fresh.fullName = newName;
  }

  // Update contacts array based on phone/email changes
  if (phoneChanged || emailChanged) {
    let contacts = [...(originalEntity.contacts || [])];

    if (phoneChanged) {
      if (existPhoneId && cleanPhone) {
        // Update existing phone
        contacts = contacts.map(c =>
          c._id === existPhoneId ? { ...c, value: cleanPhone, countryCode: phoneCC } : c
        );
      } else if (!existPhoneId && cleanPhone) {
        // Add new phone
        contacts.push({
          contactType: 'phone',
          value: cleanPhone,
          countryCode: phoneCC,
          isPrimary: true,
        });
      } else if (existPhoneId && !cleanPhone) {
        // Remove phone
        contacts = contacts.filter(c => c._id !== existPhoneId);
      }
    }

    if (emailChanged) {
      if (existEmailId && cleanEmail) {
        // Update existing email
        contacts = contacts.map(c => (c._id === existEmailId ? { ...c, value: cleanEmail } : c));
      } else if (!existEmailId && cleanEmail) {
        // Add new email
        contacts.push({
          contactType: 'email',
          value: cleanEmail,
          countryCode: 'BR',
          isPrimary: true,
        });
      } else if (existEmailId && !cleanEmail) {
        // Remove email
        contacts = contacts.filter(c => c._id !== existEmailId);
      }
    }

    fresh.contacts = contacts;
  }

  // Update virtual fields (phone/email) for fallback render compatibility
  const phoneContact = (fresh.contacts || []).find(c => c.contactType === 'phone');
  const emailContact = (fresh.contacts || []).find(c => c.contactType === 'email');
  fresh.phone = phoneContact || null;
  fresh.email = emailContact || null;

  return fresh;
}
